import { useContext, JSX } from 'react';
import { ChatMessages } from './screens/ChatMessages/ChatMessages';
import { FormScreen } from './screens/Form/FormScreen';
import { SurveyScreen } from './screens/Survey/SurveyScreen';
import { ChatToggler } from '../components/chatToggler/ChatToggler';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { PopupMessage } from './Messages/PopupMessage';
import { frameStateContext } from '../contexts/frame/context';
import { useNewMessagesNotification } from '../hooks/useNewMessagesNotification';
import { OrientationAlert } from './orientationAlert/OrientationMessage';
import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import { ChatXPosition } from '@enuan/chat__utils';
import { configurationContext } from 'src/contexts';

export const Chat = styled(
	({ className, ...props }: Omit<JSX.IntrinsicElements['div'], 'children'>) => {
		const { xPosition = ChatXPosition.RIGHT, toggler_button: { hidden } = {} } =
			useContext(configurationContext);

		const {
			isStandalone,
			isOpen: { isOpen },
			hidePopup,
			isFullscreen,
			isDetached,
			page,
		} = useContext(frameStateContext);

		const { isMobile } = useBreakpoint();
		const isMobileOpen = isMobile && isOpen;
		const isFullscreenOpen = isFullscreen && isOpen;
		const isHidden = hidden || isStandalone;
		const showFloater = !isMobileOpen && !isFullscreenOpen && !isHidden;

		useNewMessagesNotification();

		const $isFullscreen = isStandalone || isFullscreen;

		return (
			<div
				className={classNames(
					'chat',
					xPosition && `chat--${xPosition}`,
					isMobile && 'chat--mobile',
					$isFullscreen && 'chat--fullscreen',
					isOpen && 'chat--open',
					isDetached && 'chat--detached',
					className,
				)}
				{...props}
			>
				<OrientationAlert />
				{isOpen && page === 'messages' && <ChatMessages />}
				{isOpen && page === 'form' && <FormScreen />}
				{isOpen && page === 'survey' && <SurveyScreen />}
				{!hidePopup && <PopupMessage />}
				{showFloater && <ChatToggler />}
			</div>
		);
	},
	{ target: 'Chat-root', label: 'Chat' },
)({
	'--chat__toggler_size': '6.36rem',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'safe flex-end',
	justifyContent: 'flex-end',
	pointerEvents: 'none',
	zIndex: 1,
	height: '100%',
	fontFamily: "'Ubuntu', sans-serif",
	'& *': {
		WebkitFontSmoothing: 'antialiased',
		MozOsxFontSmoothing: 'grayscale',
	},
	'&:not(.chat--detached)': {
		position: 'fixed',
		'&.chat--fullscreen': {
			position: 'absolute',
		},
	},
	'&.chat--right,	&.chat--left': {
		bottom: '2em',
		maxHeight: 'calc(100% - calc(2em * 2))',
	},
	'&.chat--right': {
		right: '3em',
		'& .floater': {
			marginLeft: 'auto',
		},
	},
	'&.chat--left': {
		left: '3em',
		'& .floater': {
			marginRight: 'auto',
		},
	},
	'&.chat--mobile': {
		maxHeight: 'calc(100% - calc(2.4em * 2))',
		bottom: '2.4em',
		'&.chat--right': {
			right: '2em',
		},
		'&.chat--left': {
			left: '2em',
		},
		'& .header': {
			'& .header__body': {
				margin: 'calc(15px * 0.75)',
			},
			'& .header__title': {
				fontSize: '1.6em',
			},
			'& .header__description': {
				fontSize: '1.4em',
			},
			'&.header--slim': {
				'& .header__body': {
					'& svg': {
						height: '45px',
						width: '45px',
					},
					'& .header__description': {
						display: 'none',
					},
				},
			},
		},
	},
	'&.chat--mobile,	&.chat--fullscreen': {
		'&.chat--open': {
			right: 0,
			left: 0,
			top: 0,
			bottom: 0,
			padding: 0,
			alignItems: 'stretch',
			maxHeight: '100%',
		},
	},
	'&.chat--detached': {
		fontSize: 'initial',
		'& .floater': {
			margin: 'unset',
		},
		'&:not(.chat--open)': {
			display: 'block',
		},
		'&.chat--fullscreen.chat--open': {
			position: 'absolute',
			zIndex: 1000,
		},
	},
	'&:not(.chat--fullscreen):not(.chat--mobile) .chat__frame': {
		maxHeight: 'var(--chat_height, 55rem)',
		width: 'var(--chat_width, 39rem)',
		borderRadius: '15px',
		border: 'solid 1px var(--chat__frame_border)',
	},
});
